import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Navigate, useLocation } from 'react-router';
import { AndDigitalLogo } from '../../components/AndDigitalLogo';
import { Logo } from '../../components/Logo';
import { OktaWordmark } from '../../components/OktaWordmark';
import { ThemeModeToggle } from '../../components/ThemeModeToggle';
import { config, isProduction } from '../../config';
import { ENVIRONMENT_COLOUR, tokens } from '../../theme';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { beginLogin } from '../../store/authSlice';

type LoginLocationState = { returnTo?: string } | null;

/**
 * Not behind the guard, and it must never be: a protected login page is a
 * redirect loop.
 *
 * **It does not redirect on mount**, though one provider means one button and it
 * could. An automatic redirect on a page you were sent to after a failure
 * retries the failure forever, and this is also the post-logout landing.
 *
 * **Shaped after Luna's login**, so someone arriving recognises where they are
 * before reading anything.
 *
 * **The environment's colour is the only thing that says which environment this
 * is**, and it is never also printed as a word: a full screen of orange is louder
 * than a label and survives a screenshot cropped to the card.
 */
export const LoginPage = () => {
  const dispatch = useAppDispatch();
  const { status, error } = useAppSelector((state) => state.auth);
  const location = useLocation();

  const returnTo = (location.state as LoginLocationState)?.returnTo;

  // The authorize redirect is in flight. A spinner in the button rather than a
  // whole interstitial: the browser is leaving in a moment either way, and the
  // disabled button is what actually matters — it stops a second click starting
  // a competing authorize request.
  const isRedirecting = status === 'pending';

  // Someone with a live session who navigates here directly gets sent home
  // rather than being offered a sign-in they do not need.
  if (status === 'authenticated') {
    return <Navigate to="/" replace />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: ENVIRONMENT_COLOUR[config.environment],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 6,
        position: 'relative',
      }}
    >
      {/*
        Login sits outside the AppBar's chrome, so it carries its own copy of
        the toggle. **The page colour behind it does not follow the mode** — it
        says which environment you are looking at, and an environment signal
        that changed with a personal preference would stop being one. Only the
        card follows.
      */}
      <Box
        sx={{
          position: 'absolute',
          top: 24,
          right: 24,
          display: 'flex',
          // A neutral pill rather than plain `text.onBrand` on the environment
          // colour: white on the icon clears the brand red but not the polaris
          // or staging ground, and no single ink colour clears all three. The
          // pill is the same treatment `ActionPill` uses for exactly this
          // reason — an icon control that has to sit on top of anything.
          borderRadius: `${String(tokens.radius.full)}px`,
          border: 1,
          borderColor: 'border.default',
          backgroundColor: 'surface.raised',
        }}
      >
        <ThemeModeToggle />
      </Box>

      <Stack
        spacing={8}
        sx={{
          backgroundColor: 'surface.page',
          borderRadius: `${String(tokens.radius.xl)}px`,
          px: 8,
          py: 12,
          width: '100%',
          maxWidth: 368,
          alignItems: 'center',
        }}
      >
        {/*
          The one place the mark carries the product name on its own — there is
          no AppBar here, so it is not decorative. It is the same tint as the
          page behind the card, which is what ties the two together.
        */}
        <Logo size={128} label={config.appName} />

        <Typography
          variant="h1"
          component="h1"
          sx={{ color: ENVIRONMENT_COLOUR[config.environment], textAlign: 'center' }}
        >
          {config.appName}
        </Typography>

        {error !== null && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        )}

        {/*
          Blue rather than the brand red, because it is Okta's button and not
          ours — and because a red call to action on a red production page would
          disappear. `text.link` is the palette's blue; no new colour for this.
        */}
        <Button
          variant="contained"
          size="large"
          fullWidth
          disabled={isRedirecting}
          // Named explicitly: the label is half text and half image, and mid
          // redirect it is neither — the button would announce "Login with".
          aria-label="Login with Okta"
          aria-busy={isRedirecting}
          onClick={() => {
            void dispatch(beginLogin(returnTo));
          }}
          sx={{
            backgroundColor: 'text.link',
            color: 'text.onBrand',
            gap: 3,
            py: 4,
            // Poppins at 20px, matching the product name above it. The wordmark
            // beside it is an SVG sized in its own right.
            fontFamily: tokens.fontFamily.heading,
            fontSize: 20,
            fontWeight: 600,
            // Optical alignment: the wordmark's glyphs are lowercase with no
            // ascenders, so metric centring leaves it low and far from "with".
            // `> svg` and not `svg` — the busy spinner is an svg one level
            // deeper and must not be nudged with it.
            '& > svg': { marginTop: '-5px', marginLeft: '-5px' },
            '&:hover': { backgroundColor: 'text.link', filter: 'brightness(0.92)' },
            // Disabled here means "already working", not "unavailable", so it
            // keeps the colour MUI would grey out.
            '&.Mui-disabled': { backgroundColor: 'text.link', color: 'text.onBrand' },
          }}
        >
          {isRedirecting ? (
            <CircularProgress size={26} color="inherit" aria-hidden />
          ) : (
            <>
              Login with
              {/* The wordmark only. Okta's sunburst icon is dropped — the word
                  is the recognisable half, and the icon is noise at this size.
                  Beside the label rather than under it: two words on one line. */}
              <OktaWordmark height={24} />
            </>
          )}
        </Button>
      </Stack>

      {/*
        Whose tool this is, in the corner, the way Luna signs its own login.
        Knocked out on production, whose page colour is the same brand red the
        AND block is filled with — it would otherwise be an invisible rectangle.
        Hidden on a short viewport, where it would sit on top of the card.
      */}
      <Box
        sx={{
          position: 'absolute',
          right: 24,
          bottom: 24,
          display: { xs: 'none', sm: 'block' },
        }}
      >
        <AndDigitalLogo height={24} knockedOut={isProduction()} />
      </Box>
    </Box>
  );
};
